import React, { lazy } from 'react';
import { useParams } from 'react-router-dom';
import { HomeView } from '@shared/ui/home/HomeView';
import { isoDate, parseDatePath } from '@shared/ui/lib/dates';
import { Versioned } from '@shared/ui/uiVersion';
import { MainLayout } from '../layouts';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import PromoCodeLauncher from '../components/PromoCodeLauncher';
import PromoPackageSection from '../components/home/PromoPackageSection';
import { MemberCupWeekHero, cupWeekHeroOn } from '../components/home/CupWeekHero';

// Loaded only when shown, so its global CSS never reaches the new look
const ClassicHomePage = lazy(() => import('./classic/HomePage'));

/** Home page: classic, or the new look for members who picked it (see useUiVersion). */
const HomePage: React.FC = () => (
  <Versioned classic={<ClassicHomePage />}>
    <NewHomePage />
  </Versioned>
);

/** The new look: the shared view in this app's frame (ADR-0008), with this app's per-track access. */
const NewHomePage: React.FC = () => {
  const { dateString } = useParams<{ dateString?: string }>();
  const date = isoDate(parseDatePath(dateString) ?? new Date());
  const { accessibleTracks } = useSubscriptionAccess(undefined, date);
  return (
    <MainLayout>
      <HomeView
        hasTrackAccess={(m) => (accessibleTracks ?? []).includes(m.track.track_id)}
        // Cup Week: the artwork takes the promo banner's place (it sells the same Cup Week Pass)
        promoPackage={cupWeekHeroOn() ? <MemberCupWeekHero /> : <PromoPackageSection variant="banner" />}
      />
      <PromoCodeFooterLink />
    </MainLayout>
  );
};

/**
 * Redeeming needs an account, so the home page is the first place a signed-in
 * customer can act on a code - and, after the sign-in round-trip, where they
 * land. PromoCodeLauncher also reopens the modal on a code parked before
 * signing in, so it is mounted on both looks.
 */
const PromoCodeFooterLink: React.FC = () => (
  <div className="mx-auto my-8 max-w-3xl px-4 text-center">
    <PromoCodeLauncher />
  </div>
);

export default HomePage;
